// Published policy summaries, not a carrier quote or a guaranteed arrival date.
// Evidence: outputs/pdp-country-policy-audit-20261004.md in the engineering workspace.
// verifiedAt records retrieval time; the FAQ does not expose an effective date.
export const POLICY_VERIFIED_AT = '2026-10-04T10:54:38Z'

const standardReturnConditions = [
  'Items must be unworn, unwashed and unaltered, with tags and original packaging.',
  'Final-sale, clearance, marked non-returnable, hygiene and personalised items are excluded.',
]

const internationalReturnConditions = [
  ...standardReturnConditions,
  'The policy requires warehouse receipt within 30 days of delivery for a card refund.',
]

const internationalReturns = {
  summary: '30-day returns on eligible items',
  method: 'Self-shipment; return postage is paid by you.',
  conditions: internationalReturnConditions,
}

// Warnings are audit metadata. Missing timing/fees must remain unavailable;
// the component must not fill them from another country's policy.
export const DELIVERY_POLICIES = {
  th: {
    en: {
      shipping: {
        summary: 'Home delivery',
        timing:
          'Bangkok and greater Bangkok: 2–3 business days. Other provinces: 2–4 business days.',
        conditions: [
          'Holidays and campaigns may add 2–3 business days.',
          'Diamond, Black and Gold members: free home delivery with no minimum spend.',
          'For orders below ฿1,000, the published home-delivery fee is ฿39 for Pink members and ฿69 for non-Pomelo Perks customers.',
          'Final delivery fees depend on the applicable order and delivery conditions and are confirmed at checkout.',
          'Tap.Try.Buy. has separate delivery terms.',
        ],
      },
      returns: {
        summary: '30-day returns on eligible items',
        method:
          'Free drop-off at selected Pomelo stores. Other return options have separate conditions.',
        conditions: [
          'For online home-delivery purchases, the 30-day period starts on delivery.',
          ...standardReturnConditions,
          'Free store drop-off excludes Siam Paragon, Central Chidlom, Siam Premium Outlet and pop-up branches.',
          'Selected Kerry drop-off is free subject to the package-weight limit. Self-shipment costs are paid by you.',
          'Cash on Delivery and Tap.Try.Buy. have separate conditions. Voucher-applied Tap.Try.Buy. items cannot be returned after acceptance or the try-on period ends.',
        ],
      },
      sources: {
        shipping: 'https://www.pomelofashion.com/th/en/info/shipping',
        returns: 'https://www.pomelofashion.com/th/en/info/returns',
      },
      verifiedAt: POLICY_VERIFIED_AT,
      warnings: [
        'The English shipping policy says above ฿1,000; Thai says ฿1,000 and above. No active checkout threshold configuration was verified.',
        'Kerry weight-limit equality and COD wording differ between English and Thai. Do not infer a precise boundary or a universal five-day return limit.',
      ],
    },
    th: {
      shipping: {
        summary: 'จัดส่งถึงบ้าน',
        timing: 'กรุงเทพฯ และปริมณฑล: 2–3 วันทำการ ต่างจังหวัด: 2–4 วันทำการ',
        conditions: [
          'ช่วงวันหยุดและแคมเปญอาจใช้เวลาเพิ่ม 2–3 วันทำการ',
          'สมาชิก Diamond, Black และ Gold จัดส่งถึงบ้านฟรีโดยไม่มีขั้นต่ำ',
          'สำหรับออเดอร์ต่ำกว่า 1,000 บาท ค่าจัดส่งตามนโยบายคือ 39 บาทสำหรับสมาชิก Pink และ 69 บาทสำหรับลูกค้าที่ไม่ได้เป็นสมาชิก Pomelo Perks',
          'ค่าจัดส่งสุดท้ายขึ้นอยู่กับเงื่อนไขของออเดอร์และวิธีจัดส่ง โดยยืนยันที่หน้าชำระเงิน',
          'บริการ Tap.Try.Buy. มีเงื่อนไขการจัดส่งแยกต่างหาก',
        ],
      },
      returns: {
        summary: 'คืนสินค้าที่เข้าเงื่อนไขได้ภายใน 30 วัน',
        method:
          'คืนฟรีที่ร้าน Pomelo สาขาที่ร่วมบริการ ช่องทางอื่นมีเงื่อนไขแยกต่างหาก',
        conditions: [
          'สำหรับออเดอร์ออนไลน์ที่จัดส่งถึงบ้าน เริ่มนับ 30 วันจากวันที่จัดส่งสำเร็จ',
          'สินค้าต้องไม่ผ่านการใช้งาน ซัก หรือดัดแปลง พร้อมป้ายและบรรจุภัณฑ์เดิม',
          'ไม่รวมสินค้า Final Sale, Clearance สินค้าที่ระบุว่าคืนไม่ได้ สินค้าเพื่อสุขอนามัย และสินค้าสั่งทำ',
          'ไม่รวมสาขาสยามพารากอน เซนทรัลชิดลม สยามพรีเมียมเอาท์เลต และสาขา Pop-up',
          'คืนผ่าน Kerry สาขาที่ร่วมบริการฟรีตามเงื่อนไขน้ำหนักพัสดุ การส่งคืนด้วยตนเองมีค่าใช้จ่าย',
          'ออเดอร์เก็บเงินปลายทางและ Tap.Try.Buy. มีเงื่อนไขแยกต่างหาก สินค้าในออเดอร์ Tap.Try.Buy. ที่ใช้รหัสส่วนลดไม่สามารถคืนได้หลังยืนยันรับสินค้าหรือสิ้นสุดช่วงลองสินค้า',
        ],
      },
      sources: {
        shipping: 'https://www.pomelofashion.com/th/th/info/shipping',
        returns: 'https://www.pomelofashion.com/th/th/info/returns',
      },
      verifiedAt: POLICY_VERIFIED_AT,
      warnings: [
        'The English and Thai versions differ on shipping threshold equality, Kerry weight-limit equality and COD wording. No active checkout threshold configuration was verified.',
      ],
    },
  },
  sg: {
    en: {
      shipping: {
        summary: 'Home delivery',
        timing: '2–6 business days',
        conditions: [
          'Holidays and campaigns may add 1–3 business days.',
          'The delivery fee is confirmed at checkout.',
        ],
      },
      returns: {
        summary: '30-day returns on eligible items',
        method: 'Free drop-off at selected Ninja Points.',
        conditions: [
          'The return period starts on delivery.',
          ...standardReturnConditions,
          'Free return shipping applies to the selected Ninja Point drop-off service.',
        ],
      },
      sources: {
        shipping: 'https://www.pomelofashion.com/sg/en/info/shipping',
        returns: 'https://www.pomelofashion.com/sg/en/info/returns',
      },
      verifiedAt: POLICY_VERIFIED_AT,
      warnings: [
        'The shipping FAQ leaves exactly SGD40 unspecified. The active carrier threshold was not verified, so the fee and free-shipping threshold are withheld.',
        'The returns FAQ mixes Ninja Point drop-off and self-shipment refund-time wording; no refund-speed promise is provided.',
      ],
    },
  },
  my: {
    en: {
      shipping: {
        summary: 'Home delivery',
        timing:
          'West Malaysia: 3–6 business days. East Malaysia (Sabah, Sarawak and Labuan): 7–14 business days.',
        conditions: [
          'Holidays and campaigns may add 1–3 business days.',
          'The delivery fee is confirmed at checkout.',
        ],
      },
      returns: {
        summary: '30-day returns on eligible items',
        method: 'Free drop-off at selected Ninja Points.',
        conditions: [
          'The return period starts on delivery.',
          ...standardReturnConditions,
          'Free return shipping applies to the selected Ninja Point drop-off service.',
        ],
      },
      sources: {
        shipping: 'https://www.pomelofashion.com/my/en/info/shipping',
        returns: 'https://www.pomelofashion.com/my/en/info/returns',
      },
      verifiedAt: POLICY_VERIFIED_AT,
      warnings: [
        'The shipping FAQ leaves exactly RM139 unspecified. Fee and threshold display are withheld.',
        'The returns FAQ mixes Ninja Point drop-off and self-shipment refund-time wording; no refund-speed promise is provided.',
      ],
    },
  },
  au: {
    en: {
      shipping: {
        summary: 'Home delivery',
        timing: '5–7 business days',
        conditions: [
          'Holidays and campaigns may add 1–3 business days.',
          'The delivery fee is confirmed at checkout.',
        ],
      },
      returns: {
        summary: '30-day returns on eligible items',
        method: 'Self-shipment; return postage is paid by you.',
        conditions: [
          'The return period starts on delivery.',
          ...standardReturnConditions,
        ],
      },
      sources: {
        shipping: 'https://www.pomelofashion.com/au/en/info/shipping',
        returns: 'https://www.pomelofashion.com/au/en/info/returns',
      },
      verifiedAt: POLICY_VERIFIED_AT,
      warnings: [
        'Timing uses the separate Home Delivery line. The same FAQ contains misplaced Metro Manila and other-country copy.',
        'The shipping FAQ leaves exactly AUD225 unspecified. Fee and threshold display are withheld.',
      ],
    },
  },
  us: {
    en: {
      shipping: {
        summary: 'Home delivery',
        timing: '5–7 business days',
        conditions: [
          'Holidays and campaigns may add 2–3 business days.',
          'The delivery fee is confirmed at checkout.',
        ],
      },
      returns: internationalReturns,
      sources: {
        shipping: 'https://www.pomelofashion.com/us/en/info/shipping',
        returns: 'https://www.pomelofashion.com/us/en/info/returns',
      },
      verifiedAt: POLICY_VERIFIED_AT,
      warnings: [
        'The shipping FAQ leaves exactly USD150 unspecified. Fee and threshold display are withheld.',
      ],
    },
  },
  global: {
    en: {
      shipping: {
        summary: 'Delivery depends on your destination',
        conditions: [
          'Select your delivery country to see the applicable policy.',
          'Delivery options and fees are confirmed at checkout.',
        ],
      },
      returns: {
        summary: 'Return conditions depend on your destination',
        conditions: [
          'Select your delivery country and check the applicable return policy.',
          'Product eligibility and return-shipping conditions apply.',
        ],
      },
      sources: {
        shipping: 'https://www.pomelofashion.com/global/en/info/shipping',
        returns: 'https://www.pomelofashion.com/global/en/info/returns',
      },
      verifiedAt: POLICY_VERIFIED_AT,
      warnings: [
        'Global is a storefront, not one destination. Generic 5–7-day and 30-day policy statements must not override a named country policy.',
        'The global Cambodia fee note conflicts with the specific Cambodia policy. Global thresholds also omit exact equality.',
      ],
    },
  },
  hk: {
    en: {
      shipping: {
        summary: 'Home delivery',
        conditions: ['Check available delivery options at checkout.'],
      },
      returns: internationalReturns,
      sources: {
        shipping: 'https://www.pomelofashion.com/hk/en/info/shipping',
        returns: 'https://www.pomelofashion.com/hk/en/info/returns',
      },
      verifiedAt: POLICY_VERIFIED_AT,
      warnings: [
        'The only published timing is labelled Metro Manila, so no Hong Kong timing is supplied.',
        'Hong Kong is marked global-migrated. The legacy HKD fee policy and exact HKD780 boundary are not verified for the current request context.',
      ],
    },
  },
  mo: {
    en: {
      shipping: {
        summary: 'Home delivery',
        conditions: ['Check available delivery options at checkout.'],
      },
      returns: internationalReturns,
      sources: {
        shipping: 'https://www.pomelofashion.com/mo/en/info/shipping',
        returns: 'https://www.pomelofashion.com/mo/en/info/returns',
      },
      verifiedAt: POLICY_VERIFIED_AT,
      warnings: [
        'Macau uses the shared en-hk policy response. No independent Macau delivery estimate was verified; its only timing says Metro Manila.',
        'Macau is marked global-migrated. The legacy HKD fee policy is not verified for the current request context.',
      ],
    },
  },
  id: {
    id: {
      shipping: {
        summary: 'Pengiriman ke alamat tujuan',
        timing:
          'DKI Jakarta: 1–2 hari kerja. Yogyakarta: 2–6 hari kerja. Provinsi lain yang tercantum dalam kebijakan: 3–6 hari kerja.',
        conditions: [
          'Selama liburan dan kampanye, pengiriman dapat terlambat 2–3 hari kerja.',
          'Biaya pengiriman bergantung pada alamat tujuan dan dikonfirmasi saat checkout.',
        ],
      },
      returns: {
        summary: 'Pengajuan penukaran dalam 30 hari',
        method:
          'Hubungi Customer Care PT KCG terlebih dahulu. Biaya pengembalian barang untuk penukaran ditanggung pelanggan.',
        conditions: [
          'Ajukan penukaran kepada PT KCG dalam 30 hari sejak barang diterima.',
          'Hanya barang non-diskon dalam kondisi baru, dengan kemasan asli dan struk pembelian dari PT KCG.',
          'Penukaran ukuran, model atau warna bergantung pada ketersediaan stok.',
          'PT KCG menanggung biaya pengiriman barang pengganti.',
        ],
      },
      sources: {
        shipping: 'https://www.pomelofashion.com/id/id/info/shipping',
        returns: 'https://www.pomelofashion.com/id/id/info/returns',
      },
      verifiedAt: POLICY_VERIFIED_AT,
      warnings: [
        'The Indonesian policy describes exchanges, not the general refund policy used in other countries.',
        'Shipping fees vary by province; the published Bengkulu fee range is reversed. No monetary estimate is supplied.',
        'The source has an additional next-working-day sentence that conflicts with its province ranges; timing uses the explicit province table only.',
      ],
    },
  },
  kh: {
    en: {
      shipping: {
        summary: 'Home delivery',
        timing: '2–3 days',
        conditions: [
          'Delivery may be delayed in some conditions.',
          'The delivery fee is confirmed at checkout.',
        ],
      },
      returns: {
        summary: '14-day returns on eligible items',
        method:
          'Free drop-off at Pomelo stores in Cambodia. Self-shipment postage is paid by you.',
        conditions: [
          'For online home-delivery purchases, the 14-day period starts on delivery.',
          ...standardReturnConditions,
        ],
      },
      sources: {
        shipping: 'https://www.pomelofashion.com/kh/en/info/shipping',
        returns: 'https://www.pomelofashion.com/kh/en/info/returns',
      },
      verifiedAt: POLICY_VERIFIED_AT,
      warnings: [
        'The shipping policy says days, without specifying business or calendar days.',
        'The local USD50/USD1.25 policy conflicts with the global Cambodia USD100/USD15 note, and exact threshold equality is unstated. Fee and threshold display are withheld.',
      ],
    },
  },
}
